import React from "react";
import { Box, Button, Stack, Tooltip, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { palette } from "../../styles/theme";
import { LayerColorPicker, SidebarItem } from "./SpecsEditorFields/index";
import SidebarFabricEditor from "./SidebarFabricEditor";
import { LayerModel } from "./specsModel/LayerModel";
import SpecsSidebarInheritanceEditor from "./SpecsSidebarInheritanceEditor";
import { useSpecsEditorContextValue } from "./useSpecsEditor";
import type { SpecsEditorNav, SpecsLayer } from "./types";

function getSidebarLayers(layers: SpecsLayer[], nav: SpecsEditorNav) {
  if (
    nav.kind !== "element" &&
    nav.kind !== "layer" &&
    nav.kind !== "layerSpecs" &&
    nav.kind !== "positions"
  ) {
    return [];
  }

  if (!nav.part || !nav.element || !nav.style) {
    return [];
  }

  return layers.filter(
    (layer) =>
      layer.modelLayerPath?.partName === nav.part &&
      layer.modelLayerPath?.elementName === nav.element &&
      layer.modelLayerPath?.styleName === nav.style
  );
}

export default function SpecsSidebarLayerEditor() {
  const {
    nav,
    selectedPanelKind,
    allLayers,
    importSpecs,
    selectedElement,
    selectedStyleName,
    selectLayer,
    setHoveredLayer,
    removeLayer,
    updateLayerColor,
    updateLayerZIndex,
    addLayer,
    currentVersion,
  } = useSpecsEditorContextValue();
  const selectedLayerId = nav.kind === "layer" ? nav.layerId : undefined;
  const sidebarLayers = React.useMemo(
    () => getSidebarLayers(allLayers, nav),
    [allLayers, nav]
  );
  const canAddLayer = !!selectedElement && !!selectedStyleName;

  return (
    <>
      <Typography
        variant="caption"
        sx={{
          fontWeight: 600,
          color: "text.secondary",
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          fontSize: "11px",
          px: 0.5,
          mb: 0.5,
        }}
      >
        Layers
      </Typography>

      {sidebarLayers.map((layer) => (
        <React.Fragment key={layer.id}>
          <SidebarItem
            label={layer.name || "Untitled"}
            active={selectedLayerId === layer.id}
            visible={layer.visible}
            onClick={() => selectLayer(layer.id)}
            onMouseEnter={() => setHoveredLayer(layer.id)}
            onMouseLeave={() => setHoveredLayer(null)}
            onDelete={() => {
              removeLayer(layer.id);
            }}
          />
          {selectedPanelKind === "layer" && selectedLayerId === layer.id && (
            <Box sx={{ px: 1, py: 0.5, mb: 0.5 }}>
              {(() => {
                const boundLayer = layer.modelLayerPath
                  ? (currentVersion?.getLayer(layer.modelLayerPath) ?? null)
                  : null;
                const canInherit =
                  boundLayer?.layer.canInherit(boundLayer.element) ?? false;

                return (
                  <>
                    <SidebarFabricEditor
                      layer={layer}
                      modelLayer={boundLayer?.layer ?? null}
                      importSpecs={importSpecs}
                      canInherit={canInherit}
                      updateLayerColor={updateLayerColor}
                    />

                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      sx={{ mt: 0.75 }}
                    >
                      <LayerColorPicker
                        color={layer.baseColor}
                        onChange={(hex) => updateLayerColor(layer.id, hex)}
                      />
                      {canInherit ? (
                        <Button
                          size="small"
                          variant={
                            LayerModel.isInheritedColor(layer.baseColor)
                              ? "contained"
                              : "outlined"
                          }
                          onClick={() =>
                            updateLayerColor(
                              layer.id,
                              LayerModel.INHERITED_COLOR_NAME
                            )
                          }
                          sx={{
                            minWidth: 0,
                            px: 1,
                            py: 0.25,
                            fontSize: "11px",
                            textTransform: "none",
                          }}
                        >
                          Inherit
                        </Button>
                      ) : null}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: "10px",
                            color: "text.secondary",
                            whiteSpace: "nowrap",
                          }}
                        >
                          z
                        </Typography>
                        <Box
                          component="input"
                          type="number"
                          value={layer.zIndex}
                          onChange={(
                            e: React.ChangeEvent<HTMLInputElement>
                          ) => {
                            const v = parseInt(e.target.value, 10);
                            if (!isNaN(v)) updateLayerZIndex(layer.id, v);
                          }}
                          sx={{
                            width: 56,
                            border: "1px solid rgba(0,0,0,0.15)",
                            borderRadius: "4px",
                            fontSize: "11px",
                            fontFamily: "monospace",
                            px: 0.5,
                            py: 0.25,
                            outline: "none",
                            "&:focus": {
                              borderColor: palette.brand.primary,
                            },
                          }}
                        />
                      </Box>
                    </Stack>

                    <SpecsSidebarInheritanceEditor />
                  </>
                );
              })()}
            </Box>
          )}
        </React.Fragment>
      ))}

      <Tooltip
        title={
          canAddLayer ? "" : "Select an element and style before adding a layer"
        }
      >
        <Box sx={{ mt: 1 }}>
          <Button
            variant="outlined"
            size="small"
            fullWidth
            startIcon={<AddIcon />}
            disabled={!canAddLayer}
            onClick={() => addLayer()}
            sx={{ textTransform: "none" }}
          >
            Add Layer
          </Button>
        </Box>
      </Tooltip>
    </>
  );
}
